/**
 * Ledger event types, as sentences (ADR-118).
 *
 * The activity log printed each row as `eventType — reason`, and the envelope
 * has no `reason` field, so the fallback made every line read
 * "stage.created — stage.created". Under that, actors were role-instance UUIDs.
 * The result was a page where every row said the same machine word twice and
 * named nobody: unreadable, not merely dense.
 *
 * The phrases below are the platform's own vocabulary translated once. Two
 * rules kept them honest:
 *
 *  1. **The distinctions the system makes are preserved.** "requested a move"
 *     and "the board confirmed the move" are different events for a reason —
 *     §8.3.9's read-back barrier is the whole difference between a stage that
 *     asked and a stage that moved — so they read differently here.
 *  2. **Nothing is invented.** An event with no phrase falls back to its type
 *     rather than to a guess. A wrong sentence in an audit trail is worse than
 *     an untranslated one, because it reads as though somebody checked.
 */

/** `{actor}` is substituted with the actor's name, or dropped if absent. */
const PHRASES: Readonly<Record<string, string>> = {
  /* board ingest */
  'inbox.delivery.received': 'a board change arrived',
  'inbox.delivery.duplicate_suppressed': 'a repeat of a change already seen was ignored',
  'inbox.delivery.rejected': 'a board change was refused',
  'task.snapshot.observed': 'the board was read and the card had changed',
  'task.snapshot.unchanged': 'the board was read and nothing had changed',
  'task.snapshot.self_caused': 'the board change was our own edit coming back',
  'task.unmanaged.ignored': 'a card without the team marker was left alone',

  /* runs */
  'run.created': 'work started on this intent',
  'run.completed': 'the work finished',
  'run.failed': 'the work failed',
  'run.cancelled': 'the work was cancelled',
  'run.superseded': 'a human changed what the task means, so this work was superseded',

  /* stages */
  'stage.created': 'a stage was scheduled',
  'stage.claim_requested': 'a claim was written to the board',
  'stage.claim_projected': 'the board confirmed the claim',
  'stage.claim_projection_skipped': 'the claim was not projected to the board',
  'stage.claim_projection_failed': 'the claim could not be written to the board',
  'stage.dispatch_prepared': 'the stage was prepared for dispatch',
  'stage.transition_requested': 'a move was requested; the next stage waits for the board to confirm it',
  'stage.transition_committed': 'the board confirmed the move',
  'stage.transition_unknown': 'the board write outcome is undetermined and is being reconciled',
  'stage.transition_conflicted': 'the board had already moved this card elsewhere',
  'stage.blocked_human': 'the stage stopped and is waiting for a person',
  'stage.human_resolved': 'a person answered and the stage continued',
  'stage.failed': 'the stage failed',
  'stage.cancelled': 'the stage was cancelled',
  'stage.superseded': 'the stage was superseded',

  /* attempts */
  'attempt.prepared': 'an attempt was prepared',
  'attempt.delivering': 'the attempt is being handed to a worker',
  'attempt.accepted': 'a worker accepted the attempt',
  'attempt.started': '{actor} started working',
  'attempt.heartbeat': '{actor} reported it is still working',
  'attempt.output': '{actor} produced output',
  'attempt.checkpoint': 'a checkpoint was taken',
  'attempt.completed': '{actor} finished',
  'attempt.failed': '{actor} failed',
  'attempt.cancelled': 'the attempt was cancelled',
  'attempt.unknown': 'the attempt outcome is unknown',
  'attempt.unresolved': 'the attempt could not be resolved',
  'attempt.superseded': 'a newer attempt replaced this one',
  'attempt.late_result_rejected': 'a result arrived after its lease expired and was refused',

  /* leases */
  'lease.granted': '{actor} took the lease',
  'lease.renewed': 'the lease was renewed',
  'lease.released': 'the lease was released',
  'lease.expired': 'the lease expired',
  'lease.revoked': 'the lease was revoked',
  'lease.superseded': 'the lease was superseded by a newer one',

  /* proof */
  'evidence.recorded': '{actor} signed evidence',
  'evidence.rejected_stale': 'evidence signed against a superseded revision was refused',
  'gate.evaluated': 'a gate was evaluated',
  'approval.recorded': 'an approval was recorded',
  'approval.invalidated': 'a new revision invalidated an approval',

  /* people */
  'human.question.raised': 'a question was raised for a person',
  'human.decision.recorded': '{actor} recorded a decision',
  'rework.returned': 'the task was sent back for rework',
  'rework.budget_exhausted': 'the task ran out of rework attempts',
  'rework.escalated_human': 'the rework was escalated to a person',

  /* board writes */
  'outbox.enqueued': 'a board write was queued',
  'outbox.confirmed': 'the board write was confirmed',
  'outbox.failed': 'the board write failed',
  'outbox.unknown': 'the board write outcome is unknown',
  'outbox.marker_found': 'the write was found on the board by its marker',
  'outbox.marker_absent': 'the write was not on the board',
  'outbox.abandoned': 'the board write was abandoned',

  /* providers and threads */
  'routing.decided': 'a provider route was chosen',
  'provider.session.created': 'a new provider session was opened',
  'provider.session.resumed': 'the provider session was resumed',
  'provider.session.lost': 'the provider session was lost; continuity was downgraded',
  'provider.rate_limited': 'the provider reported a limit',
  'thread.entry.appended': 'an entry was added to the thread',
  'thread.compacted': 'the thread was compacted',
  'thread.context_pack.built': 'a context pack was built for a new session',
  'memory.promoted': 'something was promoted into longer-lived memory',

  /* machinery */
  'workspace.allocated': 'a workspace was allocated',
  'workspace.quarantined': 'a workspace was quarantined',
  'workspace.released': 'a workspace was released',
  'tool.invoked': '{actor} used a tool',
  'tool.denied': 'a tool call was refused by policy',
  'tool.completed': 'a tool call finished',
  'resource.reaped': 'resources were reclaimed',
  'resource.leak_detected': 'a leaked resource was found',

  /* configuration */
  'team.version.drafted': 'a team version was drafted',
  'team.version.validated': 'a team version was validated',
  'team.version.activated': 'a team version was activated',
  'team.paused': 'the team was paused',
  'team.resumed': 'the team was resumed',
  'role.paused': 'a role was paused',
  'role.resumed': 'a role was resumed',
  'node.enrolled': 'a worker node enrolled',
  'node.heartbeat': 'a worker node reported in',
  'node.unreachable': 'a worker node stopped answering',
  'incident.opened': 'an incident was opened',
  'incident.closed': 'an incident was closed',
  'integrity.violation_detected': 'an integrity violation was detected',
};

/**
 * One event as a sentence.
 *
 * `actorName` is the role's configured name where the caller could resolve one;
 * when it is still a raw identifier the phrase drops the actor rather than
 * printing a UUID mid-sentence.
 */
export function describeEvent(eventType: string, actorName: string | null): string {
  const phrase = PHRASES[eventType];
  if (phrase === undefined) return eventType;

  if (!phrase.includes('{actor}')) return phrase;

  const looksLikeAnId = actorName === null || /^[0-9a-f]{8}-[0-9a-f]{4}-/iu.test(actorName);
  return looksLikeAnId
    ? phrase.replace('{actor} ', '').replace(/^./u, (first) => first.toLowerCase())
    : phrase.replace('{actor}', actorName);
}

/** True when this event is worth showing to somebody who is not debugging. */
export function isNoise(eventType: string): boolean {
  /*
   * Heartbeats and unchanged reads are the two the platform emits on a timer.
   * They are the majority of any ledger and they say only that the machinery is
   * running, which the health line already says in one place.
   */
  return eventType === 'attempt.heartbeat' || eventType === 'node.heartbeat' || eventType === 'task.snapshot.unchanged';
}

/**
 * A stage's state, as a phrase and a tone.
 *
 * Same rule as the event phrases: the machine word stays on screen next to the
 * phrase, because it is the string you grep the event store for. What changes
 * is that the phrase, not the enum, is what a person reads first.
 */
export interface StagePhrase {
  readonly text: string;
  readonly tone: 'active' | 'wait' | 'danger' | 'info' | 'neutral';
}

const STAGE_STATES: Readonly<Record<string, StagePhrase>> = {
  PENDING: { text: 'waiting to start', tone: 'neutral' },
  CLAIM_REQUESTED: { text: 'claiming the card', tone: 'wait' },
  CLAIM_CONFIRMED: { text: 'claimed', tone: 'info' },
  DISPATCHED: { text: 'handed to a worker', tone: 'info' },
  RUNNING: { text: 'running', tone: 'active' },
  // §8.3.9: asked, and not yet read back. The next stage is not eligible.
  TRANSITION_REQUESTED: { text: 'waiting for the board', tone: 'wait' },
  TRANSITION_COMMITTED: { text: 'done', tone: 'active' },
  TRANSITION_UNKNOWN: { text: 'outcome unknown', tone: 'danger' },
  BLOCKED_HUMAN: { text: 'waiting for a person', tone: 'wait' },
  FAILED: { text: 'failed', tone: 'danger' },
  CANCELLED: { text: 'cancelled', tone: 'neutral' },
  SUPERSEDED: { text: 'superseded', tone: 'neutral' },
};

export function describeStage(state: string): StagePhrase {
  return STAGE_STATES[state] ?? { text: state, tone: 'neutral' };
}
